// @givmo/fundraisers-module — every line the season setup (FCM-WEB34), a campaign's pledge template, a game's
// featured fundraiser and the Layers tab's season acts show: the mockups Jonathan ruled (D-FCM-61,
// 2026-10-22 19:42 PDT, all eight decisions as drawn; `scratchpad/2026-10-22-fcm-web34-season-setup-mockups/`),
// as D-GFU-57 amended them (2026-10-23: pledging opens when a pledge is published, so there is no "when
// pledging opens" choice or label), with D-FCM-65's Terms of Use line (2026-10-23 07:24 PDT), and D-FCM-71's
// shorter acts, Add games' own words and "Done" after a partial answer (2026-10-27 14:40 PDT; headings and
// titles keep their words). Lines the set marks REUSED keep their source's words. A refusal is never
// reworded here: the page shows the server's own sentence from its answer (D-FCM-69's refusal of a season
// total among them).
import { possessive } from './layers-copy';

export const SEASON_COPY = {
  // ── The Layers tab (decision 1) ─────────────────────────────────────────────────────────────────
  /** The page's title. */
  setUp: 'Set up a season',
  /** The empty tree's act (D-FCM-71). */
  setUpAct: 'Set up season',
  setUpLine:
    'For a season of games, set up the schedule and the pledge once, and every game is made for you.',
  /** The tree header's act once the season is set up, the page's title from it, and its act (D-FCM-71). */
  addGames: 'Add games',
  /** The module's toast pattern (`layers-copy.ts`' "Layer added."). */
  gamesSetUp: (count: number) => (count === 1 ? '1 game set up.' : `${count} games set up.`),

  // ── The page (decision 2) ───────────────────────────────────────────────────────────────────────
  subtitle: (season: string) =>
    `Add ${possessive(season)} games. Each one gets its own page under it, with its scoreboard and the nightly pledge.`,
  seasonTitle: 'The season',
  /** RULED (decision 2, D-FCM-50's own example): what the season calls the campaigns inside it, filled in. */
  gameSingular: 'Game',
  gamePlural: 'Games',
  deadlineFromLastGame: 'Filled in with the last game’s day. You can move it later.',
  // A no-break space keeps "11:59 PM" on one line (tester-5's item 12).
  scheduleSubtitle:
    'Each game gets its own page under this season, and ends at 11:59\u00a0PM on its day.',
  timesIn: (zone: string) => `Times are in ${zone}.`,
  /** REUSED: `packages/employer-module/src/features/give/give-history-table.tsx`. */
  colDate: 'Date',
  colTime: 'First pitch',
  colOpponent: 'Opponent',
  colPage: 'Game page',
  /** Decision 8: a game's page is its matchup and its day, since a series repeats a matchup. */
  vsName: (team: string, opponent: string) => `${team} vs ${opponent}`,
  gamePageName: (matchup: string, day: string) => `${matchup}, ${day}`,
  nightlyName: (matchup: string, day: string) => `${matchup} nightly pledge, ${day}`,
  /** A game before its opponent is typed. */
  gameN: (index: number) => `Game ${index}`,
  noGames: 'No games yet',
  noGamesBody: 'Add each game with its date, first pitch and opponent.',
  addGame: 'Add a game',
  gameCount: (count: number) => (count === 1 ? '1 game' : `${count} games`),
  gameRemoved: (day: string) => `Removed the ${day} game.`,
  undo: 'Undo',
  finishOrRemove: 'Finish this game or remove it.',
  /** RULED (D-FCM-61's copy list): a press with no game typed in full. */
  addAtLeastOne: 'Add at least one game.',

  // ── The scoreboard (decision 4) ─────────────────────────────────────────────────────────────────
  scoreboardTitle: 'Scoreboard',
  /** REUSED: iOS `PledgeCreatorCopy.swift` (`noSportsFeedYet`). */
  scoreboardSubtitle: "There's no licensed sports data feed yet. Pledgors are told you enter it.",
  /** REUSED: the iOS pledge creator's approved words (`PledgeCreatorCopy.swift`, `PledgeCopy.swift`). */
  whosCounted: "Who's being counted?",
  whatCounted: 'What are you counting?',
  unitPlaceholder: 'point scored',
  writeItAsOne: 'Write it as one: point, mile, goal.',
  mostExpected: (units: string) => `Most ${units} you'd realistically expect`,
  mostExpectedFallback: "Most you'd realistically expect",
  mostChecked: 'Anything higher is checked before anyone is charged.',
  howItReads: 'How it reads in a sentence',
  optional: 'Optional',
  howItReadsHelper: 'Leave it empty to use the standard wording.',
  exactlyWhatCounts: 'Exactly what counts',
  rulePlaceholder: 'Every point scored in league games, playoffs included',
  bothSidesTitle: 'On each game’s scoreboard',
  sideTeam: (units: string) => `${units} · pledges count these`,
  opponentLabel: 'Each game’s opponent',
  sideOpponent: (team: string | null, units: string) =>
    team
      ? `${units} · shown beside them in the score, “${team} 3 – Eagles 1”`
      : `${units} · shown beside them in the score`,
  yourTeam: 'Your team',
  unitsFallback: 'points',

  // ── The nightly pledge (decisions 3 and 5) ──────────────────────────────────────────────────────
  nightlyTitle: 'Nightly pledge',
  /** D-FCM-53's up-front words: where a pledge naming who's counted goes. */
  reachSeason: (season: string) =>
    `It goes on every game in ${season}. It names who’s counted, so it’s never added to a page someone starts by joining.`,
  /** REUSED: the iOS pledge creator's approved words. */
  howTheAmountWorks: 'How the amount works',
  forEvery: (unit: string) => `For every ${unit}`,
  whoSetsTheRate: 'Who sets the rate',
  youSuggest: 'You suggest',
  youSetIt: 'You set it',
  theyChoose: 'They choose',
  suggestedRate: 'Suggested rate',
  rate: 'Rate',
  perUnit: (unit: string) => `per ${unit}`,
  capLabel: 'Suggested starting cap — each pledgor can adjust their own',
  capNote: 'Nobody is charged more than their own cap.',
  pledgingCloses: 'Pledging closes',
  closesAtFinalOut: 'At the final out',
  closesAtFinalOutHelper:
    'When your staff end counting. If no one does, it closes at 11:59\u00a0PM that night.',
  whoReceives: 'Who receives the money',
  recipientsNote:
    "Pledges go to this fundraiser's recipients, the same as a gift does. Once someone pledges, the recipients can't change.",
  pledgorsWillSee: 'Pledgors will see',
  /** REUSED: iOS `PledgeCreatorCopy.swift` (`previewUnavailable`). */
  previewUnavailable: "We couldn't load how pledgors will see it. Try again.",
  /** D-FCM-56: the setup makes each game's nightly fundraiser the one its page shows first. */
  featuredBySetup: 'Each game’s page shows this pledge first.',
  termsFixed:
    'Once someone pledges, the terms are fixed. You can still move the dates; everyone who pledged is told.',

  // ── The campaign card (FCM-BE56, FCM-BE66: the season setup picks Schedule) ─────────────────────
  cardSetOnce: 'Set here once for every game.',

  // ── What it sets up, and the act (decision 6) ───────────────────────────────────────────────────
  summaryTitle: 'What this sets up',
  summaryGames: (count: number, season: string) =>
    count === 0
      ? `Each game gets its own page under ${season}`
      : `${count === 1 ? '1 game' : `${count} games`} under ${season}, each with its own page`,
  summaryNightly: 'A nightly pledge fundraiser under each game, shown first on its page',
  summaryScoreboard: (units: string) => `A scoreboard on each game, with both teams’ ${units}`,
  summaryCard: (season: string) =>
    `Every game’s page shows ${possessive(season)} card, as a Schedule`,
  /** D-FCM-71: the act names no count. */
  submit: 'Set up games',
  /** REUSED: the module's dialogs' Cancel. */
  cancel: 'Cancel',
  /** D-FCM-71: the way out after a partial answer, since the games made are kept. */
  partlyDone: 'Done',
  settingUp: (count: number) => `Setting up ${count === 1 ? '1 game' : `${count} games`}…`,
  takesAMinute: 'This can take a minute.',
  partlyNotice: (made: number, total: number, left: number) =>
    `${made} of ${total} games were set up. ${
      left === 1 ? 'The game below wasn’t' : `The ${left} below weren’t`
    }: each says why. Fix what you can and try again.`,

  // ── D-FCM-65: the Terms of Use line, just above the act, and the Terms it opens ─────────────────
  termsBefore: 'By setting up this season, you agree to our ',
  /** D-FCM-71: Add games' own line. */
  termsBeforeAdding: 'By adding these games, you agree to our ',
  termsLink: 'Terms of Use',
  termsAfter: '.',
  /** REUSED: iOS `PledgeCreatorReviewStep.swift` (the Terms notice and its reader, D-FCM-65's pattern). */
  termsVersion: (version: number) => `Version ${version}`,
  termsDone: 'Done',
  termsLoading: 'Loading Terms of Use…',
  termsFailed: "Terms of Use couldn't be loaded. Please try again.",
  /** REUSED: the module's retry (`fundraisers-state.tsx`, `recipient-picker.tsx`); also the act after a
   * partial answer (D-FCM-71). */
  tryAgain: 'Try again',

  // ── A campaign's pledge template, on its Campaign tab (decision 3, D-FCM-53) ────────────────────
  templateTitle: 'Pledge template',
  templateSubtitle: 'A pledge this campaign adds to the pages and games under it.',
  templateNone: 'None yet.',
  /** The card's act (D-FCM-71); the card keeps its title. */
  setTemplate: 'Set template',
  saveTemplate: 'Save pledge template',
  templateSaved: 'Pledge template saved.',
  eachJoiner: 'Each person who joins',
  /** REUSED: iOS `PledgeCreatorCopy.swift`. */
  someoneElse: 'Someone else or a team',
  reachJoiner: (campaign: string, units: string) =>
    `It goes on every page someone starts by joining ${campaign}, and counts that person’s own ${units}.`,
  reachNamed: (campaign: string) =>
    `It goes on ${possessive(campaign)} own pages and games. It names who’s counted, so it’s never added to a page someone starts by joining.`,
  namedLabel: 'Team or person',
  /** REUSED: iOS `PledgeCreatorCopy.swift`. */
  starts: 'Starts',
  ends: 'Ends',
  startingCap: 'Starting cap',
  laterOnly:
    'Pages and games already made keep their own terms. A change here reaches only those added later.',
  /** REUSED: `packages/employer-module/src/features/competitions/competition-detail-page.tsx`. */
  edit: 'Edit',
  onPersonsPage: (person: string) => `On ${possessive(person)} page:`,
  onEachPage: 'On each page it goes on:',

  // ── A game's featured fundraiser (decision 5, D-FCM-56; FCM-BE117, FCM-BE121) ───────────────────
  featuredTitle: 'Featured fundraiser',
  featuredLine: (fundraiser: string) => `Shown first on this game’s page: ${fundraiser}.`,
  featuredDefaultLine: (fundraiser: string) =>
    `None chosen, so the oldest fundraiser under this game with a pledge shows first: ${fundraiser}.`,
  featuredMarker: 'Featured',
  featuredDialogBody:
    'Its pledge shows first on this game’s page. The others’ pledges show as their own cards.',
  featuredNone: 'None chosen',
  featuredNoneDesc: 'The oldest fundraiser under this game with a pledge shows first.',
  /** REUSED: the module's settings toast (`campaign-settings-cards.tsx`). */
  settingsUpdated: 'Campaign settings updated',

  // ── Review-71's Q2: "Add a layer below" under a campaign carrying a pledge template (decision 7) ─
  // The sentence, "It won’t get <campaign>’s pledge template. To add games that do, use Add games.", with
  // its "Add games" a link to the page (tester-5's item 11): the words before it, then after it.
  addLayerNoTemplate: (campaign: string) =>
    `It won’t get ${possessive(campaign)} pledge template. To add games that do, use `,
  addLayerNoTemplateEnd: '.',
} as const;
